import { gql } from '@apollo/client'

const QUERY_PRODUCT = gql`
  query getProduct($id: uuid!) {
    products(where: { id: { _eq: $id } }) {
      id
      name
      sku
      slug
      type
      options
      description
      height
      width
      weight
      length
      modifier_group_arrangement
      default_vat
      default_variant_id
      archived_at
      attributes
      default_limit
      category_id
      children: product_variants(where: { deleted_at: { _is_null: true } }) {
        name
        key: id
        default_variant
        price
        vat
        image
        published
        height
        width
        weight
        length
        quantity
        options
        sku
        limit
        restrictions
        archived_at
        inserted_at
      }
    }
  }
`

const QUERY_MERCHANT_PRODUCTS = gql`
  query getMerchantProducts(
    $merchant: String!
    $active: Boolean = true
    $id: uuid
  ) {
    products(
      where: {
        merchant: { slug: { _eq: $merchant } }
        category_id: { _eq: $id }
        archived_at: { _is_null: $active }
      }
    ) {
      key: id
      name
      sku
      type
      slug
      inserted_at
      archived_at
      attributes
      children: product_variants {
        name
        key: id
        price
        vat
        image
        published
        height
        width
        weight
        length
        quantity
        sku
        limit
        restrictions
        archived_at
        inserted_at
      }
    }
  }
`

const QUERY_MERCHANT_PRODUCTS_BY_CATEGORY_SLUG = gql`
  query getMerchantProductsByCateagorySlug(
    $merchant: String!
    $active: Boolean = true
    $category_slug: String!
  ) {
    products(
      where: {
        merchant: { slug: { _eq: $merchant } }
        archived_at: { _is_null: $active }
        category: { slug: { _eq: $category_slug } }
      }
    ) {
      key: id
      name
      sku
      type
      slug
      inserted_at
      archived_at
      attributes
      children: product_variants {
        name
        key: id
        price
        vat
        image
        published
        height
        width
        weight
        length
        quantity
        sku
        limit
        restrictions
        archived_at
        inserted_at
      }
    }
  }
`

const QUERY_MERCHANT_PRODUCT_CATEGORIES = gql`
  query getMerchantProductCategories(
    $merchant: String!
    $active: Boolean = true
  ) {
    categories(
      where: {
        merchant: { slug: { _eq: $merchant } }
        archived_at: { _is_null: $active }
      }
    ) {
      key: id
      name
      slug
      archived_at
    }
  }
`

const EDIT_CATEGORY = gql`
  mutation editCategory($id: uuid!, $name: String!, $slug: String!) {
    update_categories(
      where: { id: { _eq: $id } }
      _set: { name: $name, slug: $slug, updated_at: "now()" }
    ) {
      affected_rows
    }
  }
`

const ARCHIVE_RESTORE_CATEGORY = gql`
  mutation archiveRestoreCategory($id: uuid!, $archived_at: timestamp) {
    update_categories(
      where: { id: { _eq: $id } }
      _set: { archived_at: $archived_at, source_id: null }
    ) {
      affected_rows
    }

    update_products(
      where: { category_id: { _eq: $id } }
      _set: { archived_at: $archived_at, source_id: null }
    ) {
      returning {
        id
      }
    }
  }
`

const CREATE_PRODUCT_VARIANTS = gql`
  mutation createProductVariants($values: [product_variants_insert_input!]!) {
    insert_product_variants(objects: $values) {
      affected_rows
    }
  }
`

const ADD_PRODUCT_OPTIONS = gql`
  mutation addProductOptions($product_id: uuid!, $options: [OptionParams]) {
    addOptions(productId: $product_id, options: $options) {
      id
    }
  }
`

const SOFT_DELETE_PRODUCT_VARIANT = gql`
  mutation softDeleteProductVariant($id: uuid!) {
    update_product_variants(
      where: { id: { _eq: $id } }
      _set: { published: false, deleted_at: "now()" }
    ) {
      affected_rows
    }
  }
`

const UPDATE_PRODUCT_VARIANTS = gql`
  mutation updateProductvariants(
    $id: uuid!
    $sku: String
    $price: numeric
    $vat: numeric
    $limit: Int
    $restrictions: jsonb
    $published: Boolean
    $name: String
  ) {
    update_product_variants(
      where: { id: { _eq: $id } }
      _set: {
        sku: $sku
        price: $price
        vat: $vat
        limit: $limit
        restrictions: $restrictions
        published: $published
        updated_at: "now()"
        name: $name
      }
    ) {
      affected_rows
    }
  }
`

const UPDATE_PRODUCT_VARIANT_SKU = gql`
  mutation updateProductVariantsku($id: uuid!, $sku: String) {
    update_product_variants(
      where: { id: { _eq: $id } }
      _set: { sku: $sku, updated_at: "now()" }
    ) {
      affected_rows
    }
  }
`

const ARCHIVE_RESTORE_PRODUCT_VARIANTS = gql`
  mutation archiveRestoreProductVariants(
    $id: [uuid!]
    $archived_at: timestamp
  ) {
    update_product_variants(
      where: { product_id: { _in: $id } }
      _set: { archived_at: $archived_at }
    ) {
      affected_rows
    }
  }
`

const CREATE_PRODUCT = gql`
  mutation createProduct(
    $id: uuid!
    $variant_id: uuid!
    $source_id: String
    $name: String!
    $slug: String!
    $price: numeric!
    $default_vat: numeric!
    $image: String
    $merchant_id: uuid!
    $height: numeric
    $width: numeric
    $weight: numeric
    $length: numeric
    $sku: String
    $description: String
    $default_limit: Int
    $category_id: uuid!
    $published: Boolean
    $restrictions: jsonb
  ) {
    insert_products(
      objects: {
        id: $id
        name: $name
        description: $description
        slug: $slug
        merchant_id: $merchant_id
        default_variant_id: $variant_id
        category_id: $category_id
        sku: $sku
        inserted_at: "now()"
        updated_at: "now()"
        height: $height
        width: $width
        weight: $weight
        length: $length
        default_vat: $default_vat
        default_limit: $default_limit
        type: "single"
        product_variants: {
          data: {
            id: $variant_id
            merchant_id: $merchant_id
            name: $name
            price: $price
            default_variant: true
            sku: $sku
            description: $description
            source_id: $source_id
            vat: $default_vat
            image: $image
            height: $height
            width: $width
            weight: $weight
            length: $length
            restrictions: $restrictions
            published: $published
            inserted_at: "now()"
            updated_at: "now()"
          }
        }
      }
    ) {
      affected_rows
    }
  }
`

const UPDATE_PRODUCT = gql`
  mutation updateProduct(
    $id: uuid!
    $variant_id: uuid!
    $name: String!
    $slug: String!
    $price: numeric!
    $default_vat: numeric!
    $height: numeric
    $width: numeric
    $weight: numeric
    $length: numeric
    $sku: String
    $description: String
    $default_limit: Int
    $category_id: uuid!
    $published: Boolean
    $restrictions: jsonb
  ) {
    update_products(
      where: { id: { _eq: $id } }
      _set: {
        name: $name
        description: $description
        slug: $slug
        default_variant_id: $variant_id
        category_id: $category_id
        sku: $sku
        updated_at: "now()"
        height: $height
        width: $width
        weight: $weight
        length: $length
        default_vat: $default_vat
        default_limit: $default_limit
        type: "single"
      }
    ) {
      affected_rows
    }

    update_product_variants(
      where: { id: { _eq: $variant_id } }
      _set: {
        name: $name
        price: $price
        default_variant: true
        sku: $sku
        description: $description
        vat: $default_vat
        height: $height
        width: $width
        weight: $weight
        length: $length
        restrictions: $restrictions
        published: $published
        updated_at: "now()"
      }
    ) {
      affected_rows
    }
  }
`

const DUPLICATE_PRODUCT = gql`
  mutation duplicateProduct($productId: uuid!) {
    duplicateProduct(productId: $productId) {
      id
      name
    }
  }
`

const ARCHIVE_RESTORE_PRODUCT = gql`
  mutation archiveRestoreProduct($id: uuid!, $archived_at: timestamp) {
    update_products(
      where: { id: { _eq: $id } }
      _set: { archived_at: $archived_at, source_id: null }
    ) {
      affected_rows
    }

    update_product_variants(
      where: { product_id: { _eq: $id } }
      _set: {
        archived_at: $archived_at
        source_id: null
        default_variant: false
      }
    ) {
      affected_rows
    }
  }
`

const CREATE_STORE_VARIANTS = gql`
  mutation createStoreVariants($values: [store_variants_insert_input!]!) {
    insert_store_variants(objects: $values) {
      affected_rows
    }
  }
`

const QUERY_STORE_IDS = gql`
  query queryStores($merchant: String!) {
    stores(where: { merchant: { slug: { _eq: $merchant } } }) {
      id
    }
  }
`

const CREATE_CATEGORY = gql`
  mutation createCategory(
    $id: uuid!
    $merchant_id: uuid!
    $name: String!
    $slug: String!
    $source_id: String
  ) {
    insert_categories(
      objects: {
        merchant_id: $merchant_id
        name: $name
        source_id: $source_id
        slug: $slug
        id: $id
        inserted_at: "now()"
        updated_at: "now()"
      }
    ) {
      affected_rows
    }
  }
`

const QUERY_MERCHANT_SETTING = gql`
  query queryMerchantSetting($merchant: String!) {
    merchants(where: { slug: { _eq: $merchant } }) {
      setting
    }
  }
`

const UPDATE_MERCHANT_ARRANGEMENT_SETTINGS = gql`
  mutation updateMerchantProductArrangment(
    $merchant: String!
    $arrangement: jsonb
  ) {
    update_merchants(
      where: { slug: { _eq: $merchant } }
      _append: { setting: $arrangement }
    ) {
      affected_rows
    }
  }
`

const QUERY_MERCHANT_MODIFIER_GROUPS_BY_ID = gql`
  query queryMerchantModifierGroupsByID($id: [uuid!]) {
    modifier_groups(where: { id: { _in: $id } }) {
      id
      name
      archived_at
      description
      maximum
      maximum_enabled
      minimum
      minimum_enabled
      modifier_arrangement
      modifier_group_modifiers {
        modifier {
          id
          name
          price
        }
      }
    }
  }
`

const CREATE_PRODUCT_MODIFIER_GROUPS = gql`
  mutation createMerchantProductModifierGroups(
    $values: [product_modifier_groups_insert_input!]!
    $productId: uuid
    $modifierGroupArrangement: jsonb
  ) {
    insert_product_modifier_groups(objects: $values) {
      affected_rows
    }

    update_products(
      where: { id: { _eq: $productId } }
      _set: { modifier_group_arrangement: $modifierGroupArrangement }
    ) {
      affected_rows
    }
  }
`

const DELETE_PRODUCT_MODIFIER_GROUPS = gql`
  mutation deleteProductModifierGroups(
    $productId: uuid!
    $modifierGroupId: [uuid!]
  ) {
    delete_product_modifier_groups(where: { product_id: { _eq: $productId } }) {
      affected_rows
    }
  }
`

const QUERY_MERCHANT_MODIFIER_GROUPS = gql`
  query queryMerchantModifierGroups(
    $merchant: String!
    $active: Boolean = true
  ) {
    modifier_groups(
      where: {
        merchant: { slug: { _eq: $merchant } }
        archived_at: { _is_null: $active }
      }
    ) {
      key: id
      name
      archived_at
      description
      maximum
      maximum_enabled
      minimum
      minimum_enabled
      modifier_arrangement
      modifier_group_modifiers {
        modifier {
          id
          name
          price
        }
      }
    }
  }
`

const QUERY_PRODUCT_VARIANTS_FOR_DUPLICATION = gql`
  query queryProductDataForDuplication($productId: uuid!) {
    product: products_by_pk(id: $productId) {
      slug
      merchant_id
      id
      name
      product_variants {
        published
        sku
        restrictions
        image
        length
        width
        source_id
        price
      }
    }
  }
`

const QUERY_MERCHANT_CATEGORIES = gql`
  query getMerchantCategories($merchant_id: uuid!) {
    categories(
      where: {
        merchant_id: { _eq: $merchant_id }
        archived_at: { _is_null: true }
      }
    ) {
      id
      name
    }
  }
`

const QUERY_MERCHANT_PRODUCT_LITE = gql`
  query getMerchantProductsLite($merchant_id: uuid!) {
    products(
      where: {
        merchant_id: { _eq: $merchant_id }
        archived_at: { _is_null: true }
      }
    ) {
      id
      name
    }
  }
`

const UPLOAD_IMAGE = gql`
  mutation uploadProductVariantImage($variantId: uuid!, $image: Upload!) {
    uploadProductVariantImage(
      variantId: $variantId
      image: $image
      override: true
    ) {
      id
    }
  }
`

export {
  QUERY_PRODUCT,
  QUERY_MERCHANT_PRODUCTS,
  QUERY_MERCHANT_PRODUCT_CATEGORIES,
  EDIT_CATEGORY,
  ARCHIVE_RESTORE_CATEGORY,
  CREATE_PRODUCT_VARIANTS,
  UPDATE_PRODUCT_VARIANTS,
  ADD_PRODUCT_OPTIONS,
  ARCHIVE_RESTORE_PRODUCT_VARIANTS,
  CREATE_PRODUCT,
  UPDATE_PRODUCT,
  DUPLICATE_PRODUCT,
  CREATE_CATEGORY,
  QUERY_MERCHANT_SETTING,
  UPDATE_MERCHANT_ARRANGEMENT_SETTINGS,
  CREATE_STORE_VARIANTS,
  QUERY_STORE_IDS,
  ARCHIVE_RESTORE_PRODUCT,
  QUERY_MERCHANT_MODIFIER_GROUPS_BY_ID,
  QUERY_MERCHANT_MODIFIER_GROUPS,
  CREATE_PRODUCT_MODIFIER_GROUPS,
  DELETE_PRODUCT_MODIFIER_GROUPS,
  QUERY_PRODUCT_VARIANTS_FOR_DUPLICATION,
  QUERY_MERCHANT_PRODUCTS_BY_CATEGORY_SLUG,
  QUERY_MERCHANT_CATEGORIES,
  UPLOAD_IMAGE,
  QUERY_MERCHANT_PRODUCT_LITE,
  UPDATE_PRODUCT_VARIANT_SKU,
  SOFT_DELETE_PRODUCT_VARIANT
}
